import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Dimmer, Loader } from "semantic-ui-react";
import { Button } from "components/buttons/button/Button";
import { noop } from "lodash";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router";

import { type EnumDictionary, bindAction, type ISegment } from "../../../../../interfaces";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { RolePermissions, PublishedStatusTypes, RouteNames } from "../../../../../enums";
import { useWizardStepsManager } from "../../../../../hooks/useWizardStepsManager";
import { useRtn } from "../../../../../hooks/useRtn";
import {
  MessageRevertSuccess,
  MessageMadeDraftSuccess,
  MessagePublishSuccess,
} from "../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import Segments from "components/navigation/segments/Segments";
import Restricted from "../../../../Application/Restricted";
import { EditMessageSteps } from "../types/constants";
import { DetailsHeader, RestrictedByTooltip } from "../../../../../components";
import {
  fetchDraftMessageEntity,
  publishDraftMessageEntity,
  resetMessageEntityState,
  revertMessageEntity,
} from "../state/actions/messageEntityStateActions";
import MessageDetailsSubheaderContainer from "./MessageDetailsSubheaderContainer";
import MessageInfoForm from "../Info/MessageInfoForm";
import MessageContentForm from "../Content/MessageContentForm";
import { getMessage as getMessageDetailsThunk } from "../state/thunks/messageDetailsThunk";
import { reset } from "../state/slices/messageDetailsSlice";
import { MessageModalsContainer } from "../common/MessageModalsContainer";
import { type ModalHandlers } from "../types/models";
import AssociatedPacks from "features/Licensing/Packs/AssociatedPacks/AssociatedPacks";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { AddPacksButton } from "features/Licensing/Packs/AssociatedPacks/AddPacksButton";
import MessagePerformance from "../Performance/MessagePerformance";
import { FeatureFlags } from "featureFlags";
import { withLDConsumer, type LDProps } from "launchdarkly-react-client-sdk";
import { validationSchemas } from "../../../../../utils";
import schemasUtils from "../../../../../utils/validationSchemasUtils";

const messagesOverviewUrl = `/${RouteNames.contentCommunications}/messages`;

const showLastModified = new Set([EditMessageSteps.Configuration, EditMessageSteps.Content]);
const showReportLastUpdated = new Set([EditMessageSteps.Performance]);

export const EditMessage = (props: PropsFromRedux & LDProps) => {
  const {
    messageInfo,
    messageContent,
    isDraft,
    isPurchased,
    hasBeenPublished,
    entityState,
    isLoaded,
    isLoading,
    getMessage,
    fetchDraftMessage,
    publishMessage,
    discardMessage,
    resetEntityState,
    resetMessage,
    userId,
    userPermissions,
    flags,
  } = props;
  const pageIndexes = [
    EditMessageSteps.Performance,
    EditMessageSteps.Configuration,
    EditMessageSteps.Content,
    EditMessageSteps.AssociatedPacks,
  ];
  const [activeStepIndex, setActiveStepIndex] = useState(EditMessageSteps.Performance);
  const [packsModalShown, setPacksModalShown] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const messageId = Number(params.id);
  const messageReportEnabled = !!flags?.[FeatureFlags.MessageReport];
  const [, { goToPage }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const modalHandlers = useRef<ModalHandlers>({ revert: () => noop });

  const pages: EnumDictionary<EditMessageSteps, ISegment> = useMemo(() => {
    return {
      [EditMessageSteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => {
          goToPage(EditMessageSteps.Performance);
          setActiveStepIndex(EditMessageSteps.Performance);
        },
      },
      [EditMessageSteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => {
          goToPage(EditMessageSteps.Configuration);
          setActiveStepIndex(EditMessageSteps.Configuration);
        },
      },
      [EditMessageSteps.Content]: {
        to: "content",
        label: "Content",
        onClick: () => {
          goToPage(EditMessageSteps.Content);
          setActiveStepIndex(EditMessageSteps.Content);
        },
      },
      [EditMessageSteps.AssociatedPacks]: {
        to: "packs",
        label: "Associated Packs",
        onClick: () => {
          goToPage(EditMessageSteps.AssociatedPacks);
          setActiveStepIndex(EditMessageSteps.AssociatedPacks);
        },
      },
    };
  }, [goToPage]);

  const [isInfoValid, setIsInfoValid] = useState(true);
  const [isContentValid, setIsContentValid] = useState(true);

  useEffect(() => {
    if (!isLoaded) {
      getMessage(messageId);
    }
    return () => {
      resetMessage();
      resetEntityState();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useLayoutEffect(() => {
    const handler = (_: any, activeStepIndex: EditMessageSteps) => {
      pages[activeStepIndex].init?.();
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  const reloadMessage = () => {
    getMessage(messageId);
  };

  useRtn([MessagePublishSuccess, MessageRevertSuccess, MessageMadeDraftSuccess], reloadMessage);

  const handleClose = () => {
    navigate(`/${RouteNames.contentCommunications}`);
  };

  const entityStateActions = useMemo(
    () => ({
      onPublish: () => {
        const isValid = schemasUtils.isValidSync(validationSchemas.messageContent, messageContent);
        isValid ? publishMessage(messageId) : setIsContentValid(false);
      },
      onRevert: () => {
        modalHandlers.current.revert();
      },
      onClose: handleClose,
      onEdit: () => {
        fetchDraftMessage(messageId);
      },
    }),
    [messageId, messageContent],
  );

  const handleRevert = () => {
    discardMessage(messageId);
  };

  const memoizedNavigateToCreate = useCallback(() => {
    navigate(`/${RouteNames.contentCommunications}/messages/${messageId}/send-sessions/create`);
  }, [navigate, messageId]);

  const renderAddPackButton = (hasAnyPermission: boolean) => (
    <AddPacksButton
      disabled={isDraft || isPurchased}
      hasPermission={hasAnyPermission}
      onClick={() => setPacksModalShown(true)}
    />
  );

  const renderAddSendSessionButton = (isDisabled: boolean) => {
    return (
      <RestrictedByTooltip hasPermission={!isDisabled}>
        <Button primary fluid className="control create-send" disabled={isDisabled} onClick={memoizedNavigateToCreate}>
          <span className="title">{"Send Messages"}</span>
        </Button>
      </RestrictedByTooltip>
    );
  };

  const baseUrl = `/${RouteNames.contentCommunications}/messages/${messageId}/`;
  const isValidSendUrl =
    window.location.pathname.endsWith(baseUrl + pages[EditMessageSteps.Configuration].to!) ||
    window.location.pathname.endsWith(baseUrl + pages[EditMessageSteps.Content].to!);

  const isPacksTab = window.location.pathname.endsWith("packs");
  const isPublishedAndNotPurchased = !isDraft && !isPurchased;

  return (
    <Restricted
      permissions={[RolePermissions.CommunicationsCreate]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content edit-message scrollable-content">
          <DetailsHeader
            title={messageInfo.title}
            titleForGA="Message Details"
            backButton={`/${RouteNames.contentCommunications}`}
            defaultURL={`/${RouteNames.contentCommunications}`}
            canBeEdited={isPublishedAndNotPurchased}
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!isDraft, hasBeenPublished)}
            invalidFormDetails={!isInfoValid || !isContentValid}
            isRevertVisible={hasBeenPublished}
            canBePublished={!entityState.isEntityCommandInProgress && isInfoValid && isContentValid}
            crudPermission={hasPermission}
            entityStateActions={entityStateActions}
          >
            {isValidSendUrl &&
              renderAddSendSessionButton(isDraft || !userPermissions.includes(RolePermissions.CommunicationsManage))}
            {isPacksTab && (
              <Restricted
                permissions={[RolePermissions.CommunicationsCreate, RolePermissions.PacksManage]}
                renderContent={(hasPermission) => renderAddPackButton(hasPermission)}
              />
            )}
            <AddAssetsToPackModal
              showModal={packsModalShown}
              onClose={() => setPacksModalShown(false)}
              selectedItemIds={[messageId]}
              contentType="Message"
              onComplete={noop}
            />
          </DetailsHeader>
          <MessageModalsContainer
            revertModal={{
              acceptHandler: (handler) => (modalHandlers.current.revert = handler),
              enabled: hasBeenPublished,
              userId,
              onConfirm: handleRevert,
            }}
          />
          <Dimmer active={isLoading} inverted>
            <Loader active={isLoading} />
          </Dimmer>
          <MessageDetailsSubheaderContainer
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!isDraft)}
            isUpdateInProgress={entityState.isEntityCommandInProgress && isDraft}
            lastModifiedDateTime={entityState.lastModifiedDateTime?.toString()}
            showLastModified={showLastModified.has(activeStepIndex)}
            showReportLastUpdated={showReportLastUpdated.has(activeStepIndex)}
          >
            <Segments to={`${messagesOverviewUrl}/${messageId}`}>
              {messageReportEnabled && <Segments.Segment {...pages[EditMessageSteps.Performance]} />}
              <Segments.Segment {...pages[EditMessageSteps.Configuration]} />
              <Segments.Segment {...pages[EditMessageSteps.Content]} />
              <Segments.Segment {...pages[EditMessageSteps.AssociatedPacks]} />
            </Segments>
          </MessageDetailsSubheaderContainer>
          <div className="scrollable-content">
            <Routes>
              {messageReportEnabled && (
                <Route
                  path={pages[EditMessageSteps.Performance].to}
                  element={<MessagePerformance messageName={messageInfo.title} messageId={messageId} />}
                />
              )}
              <Route
                path={pages[EditMessageSteps.Configuration].to}
                element={
                  <div className="edit-form">
                    <MessageInfoForm
                      isReadOnlyView={isPurchased}
                      disabled={!hasPermission || !isDraft}
                      onIsValidChange={setIsInfoValid}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditMessageSteps.Content].to}
                element={
                  <div className="edit-form">
                    <MessageContentForm disabled={!hasPermission || !isDraft} onIsValidChange={setIsContentValid} />
                  </div>
                }
              />
              <Route
                path={pages[EditMessageSteps.AssociatedPacks].to}
                element={
                  <AssociatedPacks
                    contentType={"Message"}
                    contentId={messageId}
                    canAddPacks={isPublishedAndNotPurchased}
                    permissions={[RolePermissions.CommunicationsCreate]}
                  />
                }
              />
              <Route
                path="*"
                element={
                  messageReportEnabled ? (
                    <Navigate to={`/${RouteNames.contentCommunications}/messages/${messageId}`} replace />
                  ) : (
                    <Navigate to={`/${RouteNames.contentCommunications}/messages/${messageId}/configuration`} replace />
                  )
                }
              />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    entityState: state.library.messages.details.messageEntityStateReducer,
    messageInfo: state.library.messages.details.values.message,
    messageContent: state.library.messages.details.values.messageContent,
    isLoading: state.library.messages.details.values.isLoading,
    isLoaded: state.library.messages.details.values.isLoaded,
    isDraft: state.library.messages.details.values.isDraft,
    isPurchased: state.library.messages.details.values.isPurchased,
    hasBeenPublished: state.library.messages.details.values.hasBeenPublished,
    userId: Number(state.userProfile.id),
    userPermissions: state.userProfile.permissions,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchDraftMessage: bindAction(fetchDraftMessageEntity, dispatch),
  publishMessage: bindAction(publishDraftMessageEntity, dispatch),
  discardMessage: bindAction(revertMessageEntity, dispatch),
  resetEntityState: bindAction(resetMessageEntityState, dispatch),
  getMessage: bindAction(getMessageDetailsThunk, dispatch),
  resetMessage: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withLDConsumer()(EditMessage));
export default ConnectedComponent;
